import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Table, Spinner, Modal } from "react-bootstrap";
import {
  FiDollarSign,
  FiTrendingUp,
  FiTruck,
  FiActivity,
  FiPlusCircle,
  FiCopy,
  FiUpload,
  FiCreditCard,
  FiClock,
  FiCheckCircle,
} from "react-icons/fi";
import axios from "axios";
import Swal from "sweetalert2";
import "../../../assets/css/delivery/earnings/DeliveryEarnings.css";
import "../../../assets/css/delivery/topup/DeliveryTopUp.css";
import DeliveryLayout from "../../../layouts/DeliveryPanelLayout";
import MyContext from "../../../context/MyContext";
import { EarningsWalletPageSkeleton } from "../../../components/general/skeleton/SkeletonBlock";

const MINIMUM_TOPUP = 500;

function DeliveryEarningsWallet() {
  const navigate = useNavigate();
  const { baseUrl, loggedInDeliveryMan, authToken } = useContext(MyContext);

  const [balance, setBalance] = useState(0);
  const [totalEarned, setTotalEarned] = useState(0);
  const [totalDeliveries, setTotalDeliveries] = useState(0);
  const [totalDeductions, setTotalDeductions] = useState(0);
  const [topups, setTopups] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminAccount, setAdminAccount] = useState(null);

  // ── NEW: admin ke set kiye hue max topup limit (System Settings >
  // Payment > "Driver Wallet Topup Limit"), viewEarningsAndWallet se
  // aati hai — null matlab admin ne koi limit set nahi ki ──
  const [topupLimit, setTopupLimit] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // Form states
  const [amount, setAmount] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Error states
  const [amountError, setAmountError] = useState("");
  const [receiptError, setReceiptError] = useState("");

  // ══════════════════════════════════════════════════════════════════════════
  // API FUNCTION — viewEarningsAndWallet
  // ══════════════════════════════════════════════════════════════════════════
  const viewEarningsAndWallet = () => {
    if (!authToken) {
      setLoading(false);
      navigate("/delivery/login");
      return;
    }
    if (!loggedInDeliveryMan?.id) return;
    setLoading(true);
    axios
      .get(`${baseUrl}/delivery/wallet/viewEarningsAndWallet`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);

        // ── FIXED: ab "error" key aur "success: false" dono cases check ho rahe hain ──
        if (
          response.data.hasOwnProperty("error") ||
          response.data.success === false
        ) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text:
              response.data.error ||
              response.data.message ||
              "Failed to load wallet.",
          });
        } else {
          // ── FIXED: fallback values taake undefined kabhi state mein na jaye ──
          setBalance(response.data.balance ?? 0);
          setTotalEarned(response.data.total_earned ?? 0);
          setTotalDeliveries(response.data.total_deliveries ?? 0);
          setTotalDeductions(response.data.total_deductions ?? 0);
          setTopups(response.data.topups ?? []);
          setEarnings(response.data.earnings ?? []);
          setTopupLimit(response.data.topup_limit ?? null);
        }
      })
      .catch((error) => {
        setLoading(false);
        if (error.response?.status === 401) {
          window.location.href = "/delivery/login";
          return;
        }
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load earnings & wallet.",
        });
      });
  };

  // ── NEW: Admin ka primary bank account — pehle hardcoded tha,
  // ab System Settings > Bank Accounts se fetch hota hai ──
  const fetchAdminBankAccount = () => {
    axios
      .get(`${baseUrl}/delivery/wallet/viewAdminBankAccount`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (!response.data.hasOwnProperty("error")) {
          setAdminAccount(response.data.admin_bank_account || null);
        }
      })
      .catch((error) => console.log(error));
  };

  // ── FIXED: sirf tab call karo jab loggedInDeliveryMan ka id available ho ──
  useEffect(() => {
    viewEarningsAndWallet();
    fetchAdminBankAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInDeliveryMan?.id, authToken]);

  const getStatusBadge = (status) => (
    <span className={`del-status-${status}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );

  const getTopupStatusBadge = (status) => (
    <span className={`dtp-status-${status}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );

  if (loading) {
    return (
      <DeliveryLayout>
        <EarningsWalletPageSkeleton />
      </DeliveryLayout>
    );
  }

  // ── FIXED: safe fallback taake balance undefined hone par bhi crash na ho ──
  const isLowBalance = (balance ?? 0) < 300;

  return (
    <DeliveryLayout>
      <div className="del-earnings-wrapper">
        <Container fluid="lg">
          {/* ── Page Title ── */}
          <div className="del-earnings-title">
            <h3>Earnings & Wallet</h3>
            <p>Track your balance, earnings, and topup history</p>
          </div>

          {/* ── Low Balance Warning ── */}
          {isLowBalance && (
            <div className="del-low-balance-alert">
              ⚠️ Low balance! Your balance is{" "}
              <strong>Rs. {(balance ?? 0).toLocaleString()}</strong>. Please
              topup to continue deliveries.
            </div>
          )}

          {/* ── Summary Cards ── */}
          <Row className="g-3 mb-4">
            <Col xs={12} sm={6} xl={3}>
              <div className="del-summary-card">
                <div className="del-summary-icon-wrap del-icon-balance">
                  <FiDollarSign size={22} />
                </div>
                <div>
                  <p className="del-summary-label">Available Balance</p>
                  <h4 className="del-summary-value">
                    Rs. {(balance ?? 0).toLocaleString()}
                  </h4>
                </div>
              </div>
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <div className="del-summary-card">
                <div className="del-summary-icon-wrap del-icon-earned">
                  <FiTrendingUp size={22} />
                </div>
                <div>
                  <p className="del-summary-label">Total Earned</p>
                  <h4 className="del-summary-value">
                    Rs. {(totalEarned ?? 0).toLocaleString()}
                  </h4>
                </div>
              </div>
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <div className="del-summary-card">
                <div className="del-summary-icon-wrap del-icon-deliveries">
                  <FiTruck size={22} />
                </div>
                <div>
                  <p className="del-summary-label">Total Deliveries</p>
                  <h4 className="del-summary-value">{totalDeliveries ?? 0}</h4>
                </div>
              </div>
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <div className="del-summary-card">
                <div className="del-summary-icon-wrap del-icon-deductions">
                  <FiActivity size={22} />
                </div>
                <div>
                  <p className="del-summary-label">Total Deductions</p>
                  <h4 className="del-summary-value">
                    Rs. {(totalDeductions ?? 0).toLocaleString()}
                  </h4>
                </div>
              </div>
            </Col>
          </Row>

          {/* ── Admin Account + Topup Button ── */}
          <div className="dtp-account-card dtp-account-card-hz mb-4">
            <Row className="align-items-center gy-3">
              <Col xs={12} lg={9}>
                <div className="dtp-account-header">
                  <FiCreditCard size={20} />
                  <h5>Admin Payment Account</h5>
                </div>
                <p className="dtp-account-note">
                  Send payment to the account below and upload your receipt.
                </p>
                {!adminAccount ? (
                  <p className="dtp-account-note">
                    Payment account not set up yet — please contact support.
                  </p>
                ) : (
                <Row className="g-2">
                  <Col xs={12} sm={6}>
                    <div className="dtp-account-detail">
                      <span className="dtp-account-label">Bank Name</span>
                      <span className="dtp-account-value">
                        {adminAccount.bankname}
                      </span>
                    </div>
                  </Col>
                  <Col xs={12} sm={6}>
                    <div className="dtp-account-detail">
                      <span className="dtp-account-label">Account Title</span>
                      <span className="dtp-account-value">
                        {adminAccount.accounttitle}
                      </span>
                    </div>
                  </Col>
                  <Col xs={12} sm={6}>
                    <div className="dtp-account-detail">
                      <span className="dtp-account-label">Account Number</span>
                      <div className="dtp-copy-row">
                        <span className="dtp-account-value dtp-mono">
                          {adminAccount.accountnumber}
                        </span>
                        <button
                          className="dtp-copy-btn"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              adminAccount.accountnumber,
                            );
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                          }}
                        >
                          <FiCopy size={14} />
                          {copied ? "Copied!" : "Copy"}
                        </button>
                      </div>
                    </div>
                  </Col>
                </Row>
                )}
              </Col>
              <Col xs={12} lg={3}>
                <div className="dtp-hz-right">
                  <div className="dtp-min-amount">
                    Minimum:{" "}
                    <strong>Rs. {MINIMUM_TOPUP.toLocaleString()}</strong>
                  </div>
                  <button
                    className="dtp-create-btn"
                    onClick={() => setShowModal(true)}
                  >
                    <FiPlusCircle size={18} />
                    Create Topup Request
                  </button>
                </div>
              </Col>
            </Row>
          </div>

          {/* ── Topup History Table ── */}
          <div className="dtp-table-card mb-4">
            <div className="dtp-table-header">
              <h5>Topup History</h5>
              <span className="dtp-table-count">{topups.length} requests</span>
            </div>
            <div className="dtp-table-responsive">
              <Table hover className="dtp-table mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Amount</th>
                    <th>Receipt</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {topups.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="dtp-empty-row">
                        No topup requests yet
                      </td>
                    </tr>
                  ) : (
                    topups.map((t, i) => (
                      <tr key={t.id}>
                        <td>{i + 1}</td>
                        <td>
                          <strong>
                            Rs. {Number(t.amount ?? 0).toLocaleString()}
                          </strong>
                        </td>
                        <td>
                          {t.receipt_image ? (
                            <a
                              href={`${baseUrl.replace("/api", "")}/storage/${t.receipt_image}`}
                              target="_blank"
                              rel="noreferrer"
                              className="dtp-receipt-link"
                            >
                              View Receipt
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>{t.created_at}</td>
                        <td>{getTopupStatusBadge(t.status)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </div>

          {/* ── Earnings/Deliveries Table ── */}
          <div className="del-table-card mb-4">
            <div className="del-table-header">
              <h5>Recent Deliveries</h5>
              <span className="del-table-count">
                {earnings.length} deliveries
              </span>
            </div>
            <div className="del-table-responsive">
              <Table hover className="del-table mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Delivery Charge</th>
                    <th>Deduction</th>
                    <th>Net Earning</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {earnings.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="del-empty-row">
                        No deliveries yet
                      </td>
                    </tr>
                  ) : (
                    earnings.map((e, i) => (
                      <tr key={e.id}>
                        <td>{i + 1}</td>
                        <td>
                          <span className="del-order-id">#{e.kmorder_id}</span>
                        </td>
                        <td>{e.kmorder?.kmcustomer?.name}</td>
                        <td>
                          Rs. {Number(e.delivery_charge ?? 0).toLocaleString()}
                        </td>
                        <td className="del-deduction">
                          - Rs. {Number(e.deduction_amount ?? 0).toLocaleString()}
                        </td>
                        <td className="del-net-earning">
                          Rs. {Number(e.net_earning ?? 0).toLocaleString()}
                        </td>
                        <td>{e.created_at}</td>
                        <td>{getStatusBadge(e.status)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </Container>

        {/* ══════════════════════════════
            TOPUP MODAL
        ══════════════════════════════ */}
        <Modal
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setAmount("");
            setReceipt(null);
            setReceiptPreview(null);
            setAmountError("");
            setReceiptError("");
          }}
          centered
          className="dtp-modal"
        >
          <Modal.Header closeButton className="dtp-modal-header">
            <Modal.Title className="dtp-modal-title">
              <FiPlusCircle size={20} />
              Create Topup Request
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="dtp-modal-body">
            {/* Amount Field */}
            <div className="dtp-form-group">
              <label className="dtp-form-label">Amount (Rs.)</label>
              <input
                type="number"
                className={`dtp-form-input ${amountError ? "dtp-input-error" : ""}`}
                placeholder={
                  topupLimit
                    ? `Rs. ${MINIMUM_TOPUP} – Rs. ${topupLimit}`
                    : `Minimum Rs. ${MINIMUM_TOPUP}`
                }
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  if (!e.target.value) {
                    setAmountError("Amount is required");
                  } else if (
                    isNaN(e.target.value) ||
                    Number(e.target.value) <= 0
                  ) {
                    setAmountError("Enter a valid amount");
                  } else if (Number(e.target.value) < MINIMUM_TOPUP) {
                    setAmountError(
                      `Minimum topup amount is Rs. ${MINIMUM_TOPUP}`,
                    );
                  } else if (topupLimit && Number(e.target.value) > topupLimit) {
                    setAmountError(
                      `Maximum topup amount is Rs. ${topupLimit}`,
                    );
                  } else {
                    setAmountError("");
                  }
                }}
              />
              {amountError && <p className="dtp-error-msg">{amountError}</p>}
            </div>

            {/* Receipt Upload */}
            <div className="dtp-form-group">
              <label className="dtp-form-label">Payment Receipt</label>
              <label className="dtp-upload-area">
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setReceipt(file);
                    setReceiptPreview(URL.createObjectURL(file));
                    setReceiptError("");
                  }}
                />
                {receiptPreview ? (
                  <img
                    src={receiptPreview}
                    alt="receipt preview"
                    className="dtp-receipt-preview"
                  />
                ) : (
                  <div className="dtp-upload-placeholder">
                    <FiUpload size={28} />
                    <p>Click to upload receipt</p>
                    <span>JPG, PNG supported</span>
                  </div>
                )}
              </label>
              {receiptError && <p className="dtp-error-msg">{receiptError}</p>}
            </div>
          </Modal.Body>
          <Modal.Footer className="dtp-modal-footer">
            <button
              className="dtp-cancel-btn"
              onClick={() => {
                setShowModal(false);
                setAmount("");
                setReceipt(null);
                setReceiptPreview(null);
                setAmountError("");
                setReceiptError("");
              }}
            >
              Cancel
            </button>
            <button
              className="dtp-submit-btn"
              onClick={() => {
                let valid = true;
                if (!amount) {
                  setAmountError("Amount is required");
                  valid = false;
                } else if (Number(amount) < MINIMUM_TOPUP) {
                  setAmountError(
                    `Minimum topup amount is Rs. ${MINIMUM_TOPUP}`,
                  );
                  valid = false;
                } else if (topupLimit && Number(amount) > topupLimit) {
                  setAmountError(
                    `Maximum topup amount is Rs. ${topupLimit}`,
                  );
                  valid = false;
                }
                if (!receipt) {
                  setReceiptError("Please upload payment receipt");
                  valid = false;
                }
                if (!valid) return;

                setSubmitting(true);
                const formData = new FormData();
                formData.append("amount", amount);
                formData.append("receipt", receipt);

                axios
                  .post(
                    `${baseUrl}/delivery/wallet/createTopupRequest`,
                    formData,
                    {
                      headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${authToken}`,
                      },
                    },
                  )
                  .then((response) => {
                    setSubmitting(false);
                    if (
                      response.data.hasOwnProperty("error") ||
                      response.data.success === false
                    ) {
                      Swal.fire({
                        icon: "error",
                        title: "Error",
                        text: response.data.error || response.data.message,
                      });
                    } else {
                      Swal.fire({
                        icon: "success",
                        title: "Success",
                        text: "Topup request submitted successfully!",
                      });
                      setShowModal(false);
                      setAmount("");
                      setReceipt(null);
                      setReceiptPreview(null);
                      setAmountError("");
                      setReceiptError("");
                      viewEarningsAndWallet();
                    }
                  })
                  .catch((error) => {
                    setSubmitting(false);
                    if (error.response?.status === 401) {
                      window.location.href = "/delivery/login";
                      return;
                    }
                    Swal.fire({
                      icon: "error",
                      title: "Error",
                      text: "Failed to submit topup request.",
                    });
                  });
              }}
              disabled={submitting}
            >
              {submitting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Submit Request"
              )}
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </DeliveryLayout>
  );
}

export default DeliveryEarningsWallet;
